import { MasteryStatus } from "@models/mastery";

export const getSimsNames = (modules) => {
  const simsNames = [];
  for (const m of modules) {
    simsNames.push(...m.simulations.map((s) => s.displayName));
  }
  return simsNames;
};

// ENG-23792: in multi-category classrooms the modules endpoint could emit the
// same module more than once, each with a fragment of its sims. A bare
// `new Map(modules.map((m) => [m.moduleId, m]))` keeps only the LAST fragment,
// silently dropping sims (e.g. Lockout & Tagout under Paint Robot). Merge the
// fragments instead, and reindex the merged sims.
export const createModulesMap = (modules) => {
  const map = new Map();
  for (const module of modules) {
    const existing = map.get(module.moduleId);
    map.set(
      module.moduleId,
      existing
        ? {
            ...existing,
            simulations: [...existing.simulations, ...module.simulations].map(
              (sim, index) => ({ ...sim, index: index + 1 })
            ),
          }
        : module
    );
  }
  return map;
};

export const StatusFilter = {
  Started: "STARTED",
  Mastered: MasteryStatus.Mastered,
  NeedsWork: MasteryStatus.NeedsWork,
  Stuck: MasteryStatus.Stuck,
  InProgress: MasteryStatus.InProgress,
  All: "ALL",
};

export const mergeMasteryInfo = (trainees, modules, masteryInfo) => {
  return modules.map((module) => ({
    ...module,
    simulations: module.simulations.map((sim) => {
      const moduleMastery = masteryInfo?.get(module.moduleId);
      const simMastery = moduleMastery?.get(sim.learningObjectiveId);
      const traineesMastery = simMastery
        ? trainees.map((t) => ({ ...t, ...simMastery.get(t.userId) }))
        : [...trainees];
      return { ...sim, mastery: traineesMastery };
    }),
  }));
};

export const mergeMasteryInfoGradebook = (trainees, modules, masteryInfo) => {
  const modulesMap = new Map();
  modules?.map((module) => {
    const moduleMastery = masteryInfo?.get(module.moduleId);
    const result = trainees.map((t) => {
      const traineeObject = { ...t };
      module.simulations.map((sim) => {
        const simMastery = moduleMastery?.get(sim.learningObjectiveId);
        const traineeMastery = simMastery?.get(t.userId);
        // Carry the identifiers the override modal/API need onto each cell,
        // since the table passes only the cell value to handleCellClick.
        traineeObject[sim.learningObjectiveId] = {
          ...traineeMastery,
          userId: t.userId,
          learningExperienceId: sim.learningExperienceId,
          learningObjectiveId: sim.learningObjectiveId,
          // Pins the update to one sim; an LE id can map to several.
          simulationCode: sim.simulationCode,
          // To be used in the override modal to display the sim and module names.
          unitTitle: module.displayName,
          title: sim.displayName,
          masteryScore: traineeMastery?.mastery,
          // Score override is only allowed on records the trainee has
          // completed. `completed` comes from mastery status, so it flips to
          // false once an override drops the score below 90; the next two
          // fields keep such a record editable.
          completed: traineeMastery?.completed,
          simCompletionCount: traineeMastery?.simCompletionCount,
          // Set when an instructor typed the score in; drives the indicator.
          scoreOverrideAt: traineeMastery?.scoreOverrideAt,
          // Content type 1 is for in-headeset/sims
          contentType: 1,
        };
      });
      return traineeObject;
    });
    modulesMap.set(module.moduleId, { ...module, users: result });
  });
  return modulesMap;
};

export const mergeCeMasteryInfo = (explorers, simulations, masteryInfo) => {
  return simulations.map((sim, index) => ({
    ...sim,
    index: index + 1,
    explorers: explorers.map((e) => {
      const simMastery = masteryInfo?.get(sim.learningExperienceId);
      const explorerMastery = simMastery?.get(e.userId) ?? {};
      return { ...e, ...explorerMastery };
    }),
  }));
};

export const mergeCeMasteryInfoWithData = (
  explorers,
  simulations,
  masteryInfo
) => {
  const data = simulations?.map((sim, index) => {
    const explorersWithMasteryData = explorers
      .map((explorer) => {
        const simMastery =
          masteryInfo?.get(sim.learningExperienceId) &&
          masteryInfo?.get(sim.learningExperienceId);
        const explorerMastery = simMastery?.get(explorer.userId);
        return explorerMastery && { ...explorer, ...explorerMastery };
      })
      .filter(Boolean);

    return {
      ...sim,
      index: index + 1,
      explorers: explorersWithMasteryData,
    };
  });

  const exploredSims = [];
  const unexploredSims = [];
  for (const sim of data) {
    masteryInfo.get(sim?.learningExperienceId)
      ? exploredSims.push(sim)
      : unexploredSims.push(sim);
  }

  return { exploredSims: exploredSims, unexploredSims: unexploredSims };
};

export const searchSimsByName = (modules, term) => {
  const filteredModules = modules
    .map((m) => {
      const filteredSims = m.simulations.filter((sim) =>
        sim.displayName.toLowerCase()?.includes(term?.toLowerCase())
      );
      return filteredSims?.length > 0 && { ...m, simulations: filteredSims };
    })
    .filter((m) => m.simulations);

  return filteredModules;
};

export const filterModules = (modules, { status, objectId, moduleId }) => {
  const filteredModules = modules
    ?.filter((m) => {
      if (moduleId) {
        return moduleId === m.moduleId ? m : undefined;
      } else {
        return m;
      }
    })
    .map((module) => {
      const filteredSims = getSimsFiltered(
        module.simulations,
        status,
        objectId
      );
      return (
        filteredSims?.length > 0 && {
          ...module,
          simulations: filteredSims,
        }
      );
    })
    .filter((m) => m.simulations);

  return filteredModules;
};

const getSimsFiltered = (simulations, status, objectId) => {
  return simulations
    .map((sim) => {
      const filteredMastery = filterUsersMasterybyStatus(sim.mastery, status);
      const foundSim = filteredMastery?.length > 0 && {
        ...sim,
        mastery: filteredMastery,
      };

      if (objectId) {
        return foundSim.learningObjectiveId === objectId ? foundSim : undefined;
      } else {
        return foundSim;
      }
    })
    .filter(Boolean);
};

const filterUsersMasterybyStatus = (mastery, status) => {
  return mastery.filter((m) => {
    switch (status) {
      case StatusFilter.All: {
        return m;
      }
      case StatusFilter.NotStarted: {
        return !m.status || m.status === status;
      }
      case StatusFilter.Started: {
        return m.status && m.status !== MasteryStatus.NotStarted;
      }
      default: {
        return m.status === status;
      }
    }
  });
};

export const mergeTrekUserMasteryInfo = (
  users,
  careersExplored,
  masteryInfo
) => {
  const usersMap = new Map(users.map((e) => [e.userId, e]));
  const careersMap = new Map();

  for (const [userId, data] of Object.entries(careersExplored)) {
    const { exploredCareers, selectedCareer, favoritedCareers, budgetPlans } =
      data;

    if (!exploredCareers) {
      continue;
    }

    for (const career of exploredCareers) {
      const careerData = careersMap.get(career.id) ?? {
        id: career.id,
        title: career.title,
        description: career.description,
        onetCode: career.onetCode,
        transfrId: career.transfrId,
        users: [],
        totalSelected: 0,
        totalViews: 0,
        totalBudgetPlans: 0,
      };

      const isFavorited = !!favoritedCareers?.some((fc) => fc.id === career.id);
      const isSelected = selectedCareer?.id === career.id;
      const isExploredVR = career.occupationCodes
        ? getUserExploredVRValue(userId, career.occupationCodes, masteryInfo)
        : undefined;

      const budgetPlan = isSelected
        ? budgetPlans?.find((bp) => bp.career.id === career.id)
        : undefined;
      const goals = isSelected
        ? { ...selectedCareer.goals, budget: budgetPlan?.goals }
        : undefined;

      const user = {
        ...usersMap.get(userId),
        isFavorited,
        isSelected,
        goals,
        isExploredVR,
        viewedAt: career.viewedAt,
        isBudgetCreated: !!budgetPlan?.budgetCompletedAt,
      };

      careerData.totalViews += 1;
      careerData.totalSelected += isSelected ? 1 : 0;
      careerData.totalBudgetPlans += budgetPlan ? 1 : 0;
      careerData.users.push(user);
      careersMap.set(career.id, careerData);
    }
  }

  return [...careersMap.values()];
};

const getUserExploredVRValue = (userId, occupationCodes, masteryInfo) => {
  for (const code of occupationCodes) {
    const simMastery = masteryInfo?.get(code);
    const userSimMastery = simMastery?.get(userId);

    // At least one sim completed to be true
    if (userSimMastery && userSimMastery.completed) {
      return true;
    }
  }

  return false;
};
